import mixpanel from 'mixpanel-browser';

/**
  Do not commit real tokens — use `.env.local` for local dev.
 */

export const MIXPANEL_EVENTS = {
  PAGE_VIEWED: 'Page Viewed',

  CONTACT_FORM_SUBMITTED: 'Contact Form Submitted',
  CONTACT_FORM_SUBMIT_ERROR: 'Contact Form Submit Error',

  SERVICE_DETAIL_VIEWED: 'Service Detail Viewed',
  SERVICE_CARD_CLICKED: 'Service Card Clicked',

  CASE_STUDY_VIEWED: 'Case Study Viewed',
  CASE_STUDY_CARD_CLICKED: 'Case Study Card Clicked'
};

let initialized = false;

export function initMixpanel() {
  const token = 'da957ce2c5fc0039a2f66410b5a10b53';
  if (!token) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        '[Mixpanel] No REACT_APP_MIXPANEL_TOKEN — add it to .env and restart the dev server.'
      );
    }
    return;
  }
  if (initialized) {
    return;
  }
  try {
    // Align with Mixpanel project setup wizard so verification + Session Replay work.
    mixpanel.init(token, {
      persistence: 'localStorage',
      autocapture: true,
      record_sessions_percent: 100,
      track_pageview: true,
      // Wizard / first-load: send at least one page view from any route (e.g. `/`).
      // Set to false if you must honor Do Not Track (some traffic may not appear in Mixpanel).
      ignore_dnt: true,
      debug: process.env.NODE_ENV === 'development'
    });

    // People (User Profiles): Events + Replays alone do not populate "Users" in the setup
    // checklist. One profile update per distinct_id satisfies Mixpanel’s user detection.
    const ref = typeof document !== 'undefined' ? document.referrer : '';
    let refDomain = '$direct';
    try {
      if (ref) refDomain = new URL(ref).hostname;
    } catch {
      refDomain = '$direct';
    }
    mixpanel.people.set_once({
      first_seen: new Date().toISOString(),
      $initial_referrer: ref || 'direct',
      $initial_referring_domain: refDomain
    });
    mixpanel.people.set({
      last_seen: new Date().toISOString()
    });

    initialized = true;
  } catch (e) {
    console.error('Mixpanel init failed:', e);
  }
}

export function isMixpanelReady() {
  return initialized;
}

/**
 * Current URL path segment context for the shared ContactForm (same form on many routes).
 */
export function getContactFormPageContext() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  if (path.startsWith('/contact-us')) return 'contact_page';
  if (path.startsWith('/service/')) return 'service_detail';
  if (path === '/services') return 'services_list';
  if (path === '/case-studies') return 'case_studies_list';
  if (path.startsWith('/case-study/')) return 'case_study_detail';
  return 'other';
}

export function track(eventName, properties) {
  if (!initialized || !eventName) {
    return;
  }
  try {
    mixpanel.track(eventName, {
      ...properties,
      $current_url: typeof window !== 'undefined' ? window.location.href : undefined
    });
  } catch (e) {
    console.error('Mixpanel track failed:', e);
  }
}

export function trackPageView(pageName, extra = {}) {
  track(MIXPANEL_EVENTS.PAGE_VIEWED, {
    page_name: pageName,
    path: typeof window !== 'undefined' ? window.location.pathname : '',
    ...extra
  });
}

/**
 * After a successful contact form submit: set Mixpanel distinct_id to the lead email and update People.
 * Email (normalized) is the identity. Call only after the server accepts the submission.
 *
 * If you need to merge pre-submit anonymous events into this profile, enable ID Merge in Mixpanel
 * and call mixpanel.alias(id) once before identify (see Mixpanel docs).
 */
export function identifyContactLead({ email, name, mobile, formPageContext }) {
  if (!initialized || !email?.trim()) {
    return;
  }
  const id = email.trim().toLowerCase();
  try {
    mixpanel.identify(id);
    mixpanel.people.set({
      $email: id,
      ...(name?.trim() ? { $name: name.trim() } : {}),
      ...(mobile ? { $phone: mobile } : {}),
      last_contact_form_context: formPageContext,
      last_contact_form_submitted_at: new Date().toISOString()
    });
  } catch (e) {
    console.error('Mixpanel identify / people.set failed:', e);
  }
}
